import { useEffect, useState } from "react";
import type { AgentScopeDTO, ConnectorDTO, ScopeAgentDTO, SubjectDTO, SubjectTypeDTO } from "../../shared/types";
import { get } from "./api";

/** One agent working under one scope, as GET /api/subjects/:id lists it. */
export type ScopeAgent = ScopeAgentDTO;

/** A scope with the agents working under it (GET /api/subjects/:id). */
export type ScopeDetail = SubjectDTO & { scopes: ScopeAgent[] };

/** One scope an agent works under (GET /api/agents/:id/scopes?detail=1). */
export type AgentScopeRow = AgentScopeDTO;

export const typePath = (key: string) => `/admin/scopes/${encodeURIComponent(key)}`;
/** A scope's page sits under its type's. */
export const scopePath = (s: { kind: string; id: string }) => `${typePath(s.kind)}/${s.id}`;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A uuid by its first eight characters; any other id as it is. */
export const shortId = (id: string) => (UUID.test(id) ? id.slice(0, 8) : id);

/** type:id with a uuid id shortened, e.g. account:5f1c3a2b. */
export function shortKey(key: string): string {
  const colon = key.indexOf(":");
  return colon < 0 ? key : `${key.slice(0, colon + 1)}${shortId(key.slice(colon + 1))}`;
}

/** What a scope is called on screen: its name, else its short id. */
export const scopeName = (s: { displayName: string; slug: string }) => s.displayName || shortId(s.slug);

/** A tool by its connector's name, else by its slug. */
export const toolName = (connectors: ConnectorDTO[], system: string) => connectors.find((c) => c.slug === system)?.name ?? system;

/** The scope types, loaded once by the screen that asks. */
/** The scope types: the live ones, which every picker offers, or with `archived` the archived ones too (the Scopes page). */
export function useScopeTypes(opts: { archived?: boolean } = {}) {
  const [types, setTypes] = useState<SubjectTypeDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = () => get<SubjectTypeDTO[]>(`/api/subject-types${opts.archived ? "?archived=1" : ""}`).then(setTypes, (e) => setError(e.message));
  useEffect(() => {
    void reload();
  }, []);
  return { types, error, reload };
}
